import React, { useState, useEffect } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, FileText, Search as SearchIcon, ChevronRight } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { sampleNotes } from '../mock';
import { useBookmarks, useProgress } from '../hooks/useLocal';
import NoteViewerPage from './NoteViewerPage';
import { API } from '../context/AuthContext';

const categoryTitles = {
  navodaya: { odia: 'ନବୋଦୟ ନୋଟ୍ସ', en: 'Navodaya Notes', color: 'from-amber-500 to-orange-600' },
  board: { odia: 'ବୋର୍ଡ ନୋଟ୍ସ', en: 'Board Notes', color: 'from-indigo-500 to-blue-600' },
  teacher: { odia: 'ଶିକ୍ଷକ ସମ୍ବଳ', en: 'Teacher Notes', color: 'from-emerald-500 to-teal-600' },
};

/**
 * NotesListPage:
 * - /notes/teacher       → DB-driven CMS resources (fully searchable, filterable)
 * - /notes/navodaya, /notes/board → legacy mock data (until Phase 2 migration)
 */
export default function NotesListPage() {
  const { category = 'navodaya' } = useParams();
  const [params, setParams] = useSearchParams();
  const subjectParam = (params.get('subject') || '').toLowerCase();
  const info = categoryTitles[category] || categoryTitles.navodaya;
  const [selected, setSelected] = useState(null);
  const [q, setQ] = useState('');

  const { isBookmarked } = useBookmarks(); // kept for legacy render
  const { isRead } = useProgress();

  if (selected) {
    return <NoteViewerPage note={selected} category={category} onBack={() => setSelected(null)} />;
  }

  if (category === 'teacher') {
    return <TeacherNotesDB info={info} initialQuery={q} setQ={setQ} q={q} initialType={params.get('type') || 'notes'} initialClassSlug={params.get('class') || ''} />;
  }

  // Legacy path (navodaya, board) — built-in sample chapters, filtered by ?subject=
  const all = sampleNotes[category] || [];
  const notes = subjectParam ? all.filter((n) => (n.subjectKey || '').toLowerCase() === subjectParam) : all;
  const subjectLabel = notes[0]?.subject || subjectParam;
  const filtered = notes.filter((n) => {
    if (!q) return true;
    return n.chapter.toLowerCase().includes(q.toLowerCase()) ||
      n.subject.toLowerCase().includes(q.toLowerCase()) ||
      (n.chapterEn || '').toLowerCase().includes(q.toLowerCase());
  });

  return (
    <div className="px-4 pt-4 space-y-4 pb-8">
      <Link to={category === 'board' ? '/board' : '/navodaya'} className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-emerald-600 font-medium">
        <ArrowLeft size={16} /> ପଛକୁ ଫେରନ୍ତୁ
      </Link>
      <div className={`rounded-3xl p-5 text-white shadow-lg bg-gradient-to-br ${info.color}`}>
        <FileText size={26} />
        <h1 className="text-xl font-extrabold mt-2">{info.odia}</h1>
        <p className="text-xs text-white/90 mt-1">{info.en} · {all.length} ଅଧ୍ୟାୟ</p>
      </div>
      {subjectParam && (
        <div className="flex items-center justify-between rounded-xl bg-emerald-50 border border-emerald-200 px-3 py-2" data-testid="notes-subject-filter">
          <span className="text-xs font-bold text-emerald-800">ବିଷୟ: {subjectLabel}</span>
          <button onClick={() => setParams({})} className="text-[11px] font-bold text-emerald-700 underline" data-testid="notes-clear-subject">ସବୁ ବିଷୟ ଦେଖନ୍ତୁ</button>
        </div>
      )}
      <div className="relative">
        <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search chapter / subject..." className="h-11 pl-9 rounded-xl" />
      </div>
      {filtered.length === 0 ? (
        <Card className="rounded-2xl p-6 text-center border-amber-200 bg-amber-50" data-testid="notes-empty">
          <div className="text-3xl">📭</div>
          <div className="text-sm font-bold text-amber-800 mt-2">{subjectParam ? 'ଏହି ବିଷୟ ପାଇଁ ଏପର୍ଯ୍ୟନ୍ତ ନୋଟ୍ସ ନାହିଁ' : 'ଏହି ବିଭାଗରେ ଏପର୍ଯ୍ୟନ୍ତ ନୋଟ୍ସ ନାହିଁ'}</div>
          <p className="text-[11px] text-amber-700 mt-1">ଅଫିସିଆଲ୍ ପାଠ୍ୟ ସାମଗ୍ରୀ ପାଇଁ <Link to={`/sources?for=${category}`} className="underline font-bold">Official Sources</Link> ଦେଖନ୍ତୁ</p>
          {subjectParam && <button onClick={() => setParams({})} className="mt-3 text-xs font-bold text-emerald-700 underline">ସବୁ ବିଷୟ ଦେଖନ୍ତୁ</button>}
        </Card>
      ) : (
        <div className="space-y-2">
          {filtered.map((n) => (
            <button key={n.id} onClick={() => setSelected(n)} className="w-full text-left p-3 rounded-xl bg-white border border-gray-200 hover:border-emerald-300 flex items-center gap-2">
              <FileText size={16} className="text-emerald-600" />
              <div className="flex-1 min-w-0"><div className="font-bold text-sm truncate">{n.chapter}</div><div className="text-[10px] text-gray-500 truncate">{n.chapterEn}</div></div>
              <ChevronRight size={14} className="text-gray-400" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const RESOURCE_TYPE_OPTIONS = [
  { value: 'notes', label: '📘 Notes' }, { value: 'lesson_plan', label: '📝 Lesson Plan' }, { value: 'worksheet', label: '📄 Worksheet' },
  { value: 'question_bank', label: '❓ Question Bank' }, { value: 'assessment', label: '📊 Assessment' }, { value: 'book', label: '📚 Book' },
  { value: 'activity', label: '🎯 Activity' }, { value: 'pdf', label: '📕 PDF' }, { value: 'image', label: '🖼 Image' }, { value: 'video', label: '🎬 Video' },
];

/** DB-driven Teacher Notes list with dependent filters */
function TeacherNotesDB({ info, q, setQ, initialType = 'notes', initialClassSlug = '' }) {
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [topics, setTopics] = useState([]);
  const [resources, setResources] = useState([]);
  const [filter, setFilter] = useState({ class_id: '', subject_id: '', topic_id: '', resource_type: RESOURCE_TYPE_OPTIONS.some(o => o.value === initialType) ? initialType : 'notes' });
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');

  // Load classes on mount (pre-select class from ?class=slug)
  useEffect(() => {
    axios.get(`${API}/discover/classes`).then(r => {
      setClasses(r.data);
      const pre = initialClassSlug && r.data.find(c => c.slug === initialClassSlug || c.id === initialClassSlug);
      if (pre) setFilter(f => ({ ...f, class_id: pre.id }));
    }).catch(() => setLoadError('Classes load ହେଲା ନାହିଁ'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Load subjects when class changes
  useEffect(() => {
    setFilter(f => ({ ...f, subject_id: '', topic_id: '' })); setTopics([]);
    if (filter.class_id) axios.get(`${API}/discover/subjects?class_id=${filter.class_id}`).then(r => setSubjects(r.data));
    else setSubjects([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter.class_id]);
  // Load topics when subject changes
  useEffect(() => {
    setFilter(f => ({ ...f, topic_id: '' }));
    if (filter.class_id && filter.subject_id) axios.get(`${API}/discover/topics?class_id=${filter.class_id}&subject_id=${filter.subject_id}`).then(r => setTopics(r.data));
    else setTopics([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter.subject_id]);

  // Load resources whenever filters change
  useEffect(() => {
    setLoading(true);
    const p = new URLSearchParams();
    if (filter.class_id) p.append('class_id', filter.class_id);
    if (filter.subject_id) p.append('subject_id', filter.subject_id);
    if (filter.topic_id) p.append('topic_id', filter.topic_id);
    if (filter.resource_type) p.append('resource_type', filter.resource_type);
    setLoadError('');
    axios.get(`${API}/discover/resources?${p}`).then(r => setResources(r.data))
      .catch(() => { setResources([]); setLoadError('Resources ଆଣିବାରେ ତ୍ରୁଟି — ନେଟୱର୍କ ଯାଞ୍ଚ କରି ପୁଣି ଚେଷ୍ଟା କରନ୍ତୁ'); })
      .finally(() => setLoading(false));
  }, [filter]);

  const filtered = q ? resources.filter(r => (r.title || '').toLowerCase().includes(q.toLowerCase()) || (r.title_odia || '').toLowerCase().includes(q.toLowerCase())) : resources;

  return (
    <div className="px-4 pt-4 space-y-4 pb-8">
      <Link to="/" className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-emerald-600 font-medium">
        <ArrowLeft size={16} /> ମୁଖ୍ୟ ପେଜ କୁ ଫେରନ୍ତୁ
      </Link>
      <div className={`rounded-3xl p-5 text-white shadow-lg bg-gradient-to-br ${info.color}`}>
        <FileText size={26} />
        <h1 className="text-xl font-extrabold mt-2">{info.odia}</h1>
        <p className="text-xs text-white/90 mt-1">DB-driven · Filter by Class → Subject → Topic</p>
      </div>

      {/* Search */}
      <div className="relative">
        <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16}/>
        <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search title..." className="h-11 pl-9 rounded-xl" data-testid="teacher-notes-search"/>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-2 gap-2">
        <select value={filter.class_id} onChange={e => setFilter({...filter, class_id: e.target.value})} data-testid="filter-class" className="h-10 rounded-lg border px-2 text-sm">
          <option value="">All Classes</option>
          {classes.map(c => <option key={c.id} value={c.id}>{c.name_odia}</option>)}
        </select>
        <select value={filter.subject_id} onChange={e => setFilter({...filter, subject_id: e.target.value})} disabled={!filter.class_id} data-testid="filter-subject" className="h-10 rounded-lg border px-2 text-sm">
          <option value="">All Subjects</option>
          {subjects.map(s => <option key={s.id} value={s.id}>{s.name_odia}</option>)}
        </select>
        <select value={filter.topic_id} onChange={e => setFilter({...filter, topic_id: e.target.value})} disabled={!filter.subject_id} data-testid="filter-topic" className="h-10 rounded-lg border px-2 text-sm col-span-2">
          <option value="">All Topics</option>
          {topics.map(t => <option key={t.id} value={t.id}>{t.name_odia}</option>)}
        </select>
        <select value={filter.resource_type} onChange={e => setFilter({...filter, resource_type: e.target.value})} data-testid="filter-type" className="h-10 rounded-lg border px-2 text-sm col-span-2">
          {RESOURCE_TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>

      {loadError && <Card className="rounded-2xl p-3 border-red-200 bg-red-50 text-xs text-red-700" data-testid="notes-load-error">{loadError}</Card>}
      {loading ? (
        <div className="text-center py-6 text-sm text-gray-500">Loading...</div>
      ) : filtered.length === 0 ? (
        <Card className="rounded-2xl p-6 text-center border-amber-200 bg-amber-50" data-testid="notes-db-empty">
          <div className="text-3xl">📭</div>
          <div className="text-sm font-bold text-amber-800 mt-2">ଏହି filter ପାଇଁ ଏପର୍ଯ୍ୟନ୍ତ content ନାହିଁ</div>
          <p className="text-[11px] text-amber-700 mt-1">{RESOURCE_TYPE_OPTIONS.find(o => o.value === filter.resource_type)?.label} — Admin CMS ରୁ ଅପଲୋଡ୍ ହେଲେ ଏଠାରେ ଦେଖାଯିବ। ଅନ୍ୟ Class/Subject/Type try କରନ୍ତୁ।</p>
          <Link to="/teacher/browse" className="mt-3 inline-block text-xs font-bold text-emerald-700 underline">Browse all resources</Link>
        </Card>
      ) : (
        <div className="space-y-2">
          <div className="text-[11px] text-gray-500">{filtered.length} resource{filtered.length !== 1 ? 's' : ''} found</div>
          {filtered.map(r => (
            <Link key={r.id} to={`/teacher/resource/${r.id}`} data-testid={`notes-resource-${r.id}`}
              className="flex items-center gap-2 p-3 bg-white rounded-xl border border-gray-200 hover:border-emerald-300 hover:bg-emerald-50">
              <FileText size={16} className="text-emerald-600 shrink-0"/>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-sm truncate">{r.title_odia || r.title}</div>
                <div className="text-[10px] text-gray-500 truncate">{r.resource_type} · {r.downloads || 0} downloads</div>
              </div>
              <ChevronRight size={14} className="text-gray-400"/>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
